import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import TariffStepDetails from '../components/TariffStepDetails';
import TariffStepPricing from '../components/TariffStepPricing';

const API = 'https://api.cpms-srlkevcharge.com';

export default function EditTariff() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [organisationName, setOrganisationName] = useState('');

  const [selectedFields, setSelectedFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const [priceComponents, setPriceComponents] = useState([]);
  const [includeIdleFee, setIncludeIdleFee] = useState(false);
  const [idleFee, setIdleFee] = useState({ idle_time: '', idle_price: '', grace_period: '' });

  useEffect(() => {
    axios.get(`${API}/organisations/`).then(res => setOrganisations(res.data)).catch(() => {});

    axios.get(`${API}/tariffs/${id}`).then(res => {
      const t = res.data;
      setOrganisationName(t.organisation_name);

      setFormData({
        name: t.name,
        currency: t.currency,
        status: t.status,
        description: t.description || '',
        tariff_code: t.tariff_code || '',
        effective_from: t.effective_from || '',
        effective_to: t.effective_to || '',
      });

      const optionalPresent = [];
      if (t.description) optionalPresent.push('description');
      if (t.tariff_code) optionalPresent.push('tariff_code');
      if (t.effective_from) optionalPresent.push('effective_from');
      if (t.effective_to) optionalPresent.push('effective_to');
      setSelectedFields(optionalPresent);

      setPriceComponents(
        t.price_components.length > 0
          ? t.price_components.map(pc => {
              const compSelected = [];
              const compFormData = {
                charging_unit: pc.charging_unit,
                price_per_unit: pc.price_per_unit,
              };
              if (pc.tax_percent) { compSelected.push('tax_percent'); compFormData.tax_percent = pc.tax_percent; }
              if (pc.minimum_charge) { compSelected.push('minimum_charge'); compFormData.minimum_charge = pc.minimum_charge; }
              if (pc.billing_increment) { compSelected.push('billing_increment'); compFormData.billing_increment = pc.billing_increment; }
              return { tempId: pc.id, formData: compFormData, selectedFields: compSelected, errors: {} };
            })
          : [{ tempId: Date.now(), formData: {}, selectedFields: [], errors: {} }]
      );

      if (t.idle_fee) {
        setIncludeIdleFee(true);
        setIdleFee({
          idle_time: t.idle_fee.idle_time || '',
          idle_price: t.idle_fee.idle_price || '',
          grace_period: t.idle_fee.grace_period || '',
        });
      }

      setLoading(false);
    }).catch(() => {
      showToast('Failed to load tariff', 'error');
      navigate('/tariffs');
    });
  }, [id]);

  const validate = () => {
    const errs = {};
    if (!formData.name?.trim()) errs.name = 'Tariff name is required';
    if (!formData.currency) errs.currency = 'Currency is required';
    if (!formData.status) errs.status = 'Tariff status is required';
    setErrors(errs);

    let componentsValid = true;
    setPriceComponents(prev => prev.map(c => {
      const cErrs = {};
      if (!c.formData.charging_unit) cErrs.charging_unit = 'Unit is required';
      if (!c.formData.price_per_unit?.trim()) cErrs.price_per_unit = 'Amount can only be a number';
      if (Object.keys(cErrs).length > 0) componentsValid = false;
      return { ...c, errors: cErrs };
    }));

    return Object.keys(errs).length === 0 && componentsValid;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      name: formData.name,
      currency: formData.currency,
      status: formData.status,
      description: formData.description || null,
      tariff_code: formData.tariff_code || null,
      effective_from: formData.effective_from || null,
      effective_to: formData.effective_to || null,
      price_components: priceComponents.map(c => ({
        charging_unit: c.formData.charging_unit,
        price_per_unit: c.formData.price_per_unit,
        tax_percent: c.formData.tax_percent || null,
        minimum_charge: c.formData.minimum_charge || null,
        billing_increment: c.formData.billing_increment || null,
      })),
      idle_fee: includeIdleFee ? idleFee : null
    };

    try {
      await axios.patch(`${API}/tariffs/${id}`, payload);
      showToast('Tariff updated successfully');
      navigate('/tariffs');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update tariff', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading tariff...</div>;

  return (
    <>
      <button className="back-link" onClick={() => navigate('/tariffs')}>← Back to Tariffs</button>

      <div className="page-header">
        <h1>Edit Tariff</h1>
        <p>Editing pricing for {organisationName}</p>
      </div>

      <TariffStepDetails
        hideOrganisation={true}
        selectedFields={selectedFields}
        setSelectedFields={setSelectedFields}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
      />

      <div style={{ marginTop: 24 }}>
        <TariffStepPricing
          priceComponents={priceComponents} setPriceComponents={setPriceComponents}
          includeIdleFee={includeIdleFee} setIncludeIdleFee={setIncludeIdleFee}
          idleFee={idleFee} setIdleFee={setIdleFee}
        />
      </div>

      <div className="wizard-actions">
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/tariffs')}>
          Cancel
        </button>
        <button type="button" className="btn btn-green" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </>
  );
}